/**
 * Pure, seedable PRNG (mulberry32). Threaded as a value: every operation
 * returns the next state alongside the produced number, so callers never
 * mutate. This is the only entropy source allowed in sim/.
 */

export type Rng = { readonly s: number };

export function createRng(seed: number): Rng {
  // Coerce to a non-zero 32-bit unsigned integer. A zero seed degenerates.
  const s = (Math.floor(seed) | 0) >>> 0;
  return { s: s === 0 ? 0x9e3779b9 : s };
}

/** Advance the RNG. Returns [uint32, nextRng]. */
export function next(rng: Rng): [number, Rng] {
  const t = (rng.s + 0x6d2b79f5) >>> 0;
  let x = t;
  x = Math.imul(x ^ (x >>> 15), x | 1);
  x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
  const out = ((x ^ (x >>> 14)) >>> 0);
  return [out, { s: t }];
}

/** Float in [0, 1). */
export function nextFloat(rng: Rng): [number, Rng] {
  const [n, r] = next(rng);
  return [n / 0x1_0000_0000, r];
}

/** Integer in [min, max) — half-open, as is convention. */
export function nextInt(rng: Rng, min: number, max: number): [number, Rng] {
  if (max <= min) throw new Error(`nextInt: empty range [${min}, ${max})`);
  const [f, r] = nextFloat(rng);
  return [min + Math.floor(f * (max - min)), r];
}

/** Pick a random element. Throws on empty arrays. */
export function pick<T>(rng: Rng, arr: readonly T[]): [T, Rng] {
  if (arr.length === 0) throw new Error('pick: empty array');
  const [i, r] = nextInt(rng, 0, arr.length);
  return [arr[i] as T, r];
}

/** Fisher–Yates shuffle, pure: returns a new array and the next rng. */
export function shuffle<T>(rng: Rng, arr: readonly T[]): [T[], Rng] {
  const out = arr.slice();
  let r = rng;
  for (let i = out.length - 1; i > 0; i--) {
    const [j, r2] = nextInt(r, 0, i + 1);
    r = r2;
    const a = out[i] as T;
    const b = out[j] as T;
    out[i] = b;
    out[j] = a;
  }
  return [out, r];
}
